import { BpmDateString } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  Forecast,
  ForecastGroup,
  ForecastGroupId,
  ForecastId,
  ForecastKpi,
  ForecastKpiViewOption,
  ForecastModelSummaryData,
  ForecastResultsData,
  getCombinedChannelPlatform,
  GetForecastDataResponse,
  getKpiOutcomeString,
  KPI_CI_VIEW_SUFFICES,
  KPI_VIEW_SUFFICES,
  KpiOutcomeString,
  OutcomeVariableType
} from '@blisspointmedia/bpm-types/dist/Forecasting';
import {
  abbreviateNumber,
  formatDecimal,
  formatMoneyAsInt,
  formatPercent
} from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  CPX_FORMATTER,
  SPEND_OR_REVENUE_FORMATTER,
  VOLUME_FORMATTER
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import {
  DynamicModelOverviewRow,
  DynamicModelOverviewSection
} from '../../Components/DynamicModelOverview/DynamicModelOverview';
import { dateToMddyy, dateToMddyyDashed, instantToUtcTimestamp } from '../../utils/date-utils';
import { downloadJSONToCSV } from '../../utils/download-utils';
import { DataQaGroup, Pairing } from './dataQaContract';
import {
  DataQaConfig,
  EMPTY_VALUE,
  FlatForecastData,
  FlattenedForecastDataResult,
  ForecastChartViewLabels,
  ForecastDataTimestampRanges,
  ForecastingNav,
  formatAxisSpend,
  formatCpm,
  formatPercentBudget,
  formatRawBudget,
  formatSpend,
  FullForecastSelection,
  GAP_KEY,
  HISTORICAL_KEY,
  SelectableForecastProperty,
  SelectableForecastPropertyValue
} from './forecasting-constants';

/**
 * The Forecasting page tabs this user can see: experimental tabs need the
 * self-serve experiment, internal-only tabs need an internal user. A tab left out
 * here also can't be reached by URL, since useTabbedNav sends an unknown tab to
 * the default (Forecast Results).
 */
export const getVisibleNavs = (
  navs: ForecastingNav[],
  { selfServe, isInternal }: { selfServe: boolean; isInternal: boolean }
): ForecastingNav[] =>
  navs.filter(
    ({ experimental, internalOnly }) =>
      (!experimental || selfServe) && (!internalOnly || isInternal)
  );

// Narrow a full list to a chosen scope, falling back to the full list when no
// scope was picked or the scope doesn't overlap. Shared by the Data QA view and
// the Request Forecast form so both flows narrow the same way.
export const narrowToScope = (all: string[], pick?: string[]): string[] => {
  const inScope = pick ? all.filter(x => pick.includes(x)) : all;
  return inScope.length ? inScope : all;
};

// Narrow a run's groups (and their channel/KPI axes) to a saved scope. Shared by
// the Data QA view and the Request Forecast banner so their combination counts
// stay in sync.
export const scopeQaGroups = (
  groups: DataQaGroup[],
  savedScope?: DataQaConfig
): { channels: string[]; kpis: string[]; groups: DataQaGroup[] } => {
  const channels = narrowToScope([...new Set(groups.map(g => g.channel))], savedScope?.channels);
  const kpis = narrowToScope([...new Set(groups.map(g => g.kpi))], savedScope?.kpis);
  return {
    channels,
    kpis,
    groups: groups.filter(g => channels.includes(g.channel) && kpis.includes(g.kpi))
  };
};

export const formatModelOverviewData: (
  modelSummaryData: ForecastModelSummaryData | undefined,
  kpiType: OutcomeVariableType | undefined,
  kpiName: string | undefined,
  primaryBudget: number | undefined,
  lastRefreshDate: BpmDateString | undefined
) => DynamicModelOverviewSection[] = (
  modelSummaryData,
  kpiType,
  kpiName,
  primaryBudget,
  lastRefreshDate
) => {
  if (!modelSummaryData || !kpiType || !primaryBudget || !lastRefreshDate) {
    return [];
  }
  const kpiLabel = kpiName ?? '';

  const {
    total_estimate,
    total_estimate_lower,
    total_estimate_upper,
    efficiency_estimate,
    efficiency_estimate_lower,
    efficiency_estimate_upper,
    model_start,
    model_end,
    avg_spend,
    spend_relationship,
    last_fold_mae,
    last_fold_wmape,
    median_mae,
    median_wmape,
    test_split,
    train_split
  } = modelSummaryData;

  const formatTotalKpi = getKpiFormatter(kpiType, ForecastKpiViewOption.TOTAL);
  const formatEfficiencyKpi = getKpiFormatter(kpiType, ForecastKpiViewOption.EFFICIENCY);
  const keyForecastTotalValue = (
    <div>
      {formatTotalKpi(total_estimate)}{' '}
      <span className='confidence-range'>
        ({formatTotalKpi(total_estimate_lower)}&ndash;{formatTotalKpi(total_estimate_upper)})
      </span>
    </div>
  );

  // A "better" CPA is a smaller number, which we want to show first in the range for UX understanding
  const lowEffCi = kpiType === 'Revenue' ? efficiency_estimate_lower : efficiency_estimate_upper;
  const highEffCi = kpiType === 'Revenue' ? efficiency_estimate_upper : efficiency_estimate_lower;
  const keyForecastEfficiencyValue = (
    <div>
      {formatEfficiencyKpi(efficiency_estimate)}{' '}
      <span className='confidence-range'>
        ({formatEfficiencyKpi(lowEffCi)}&ndash;
        {formatEfficiencyKpi(highEffCi)})
      </span>
    </div>
  );

  const keyForecastRows: DynamicModelOverviewRow[] = [
    {
      label: formatForecastViewLabel(kpiType, kpiLabel, ForecastKpiViewOption.TOTAL),
      value: keyForecastTotalValue
    },
    {
      label: ForecastChartViewLabels[kpiType][ForecastKpiViewOption.EFFICIENCY],
      value: keyForecastEfficiencyValue
    }
  ];

  return [
    {
      title: 'Training Data',
      rows: [
        { label: 'First model observation', value: dateToMddyy(model_start) },
        { label: 'Last model observation', value: dateToMddyy(model_end) },
        { label: 'Average daily spend', value: formatMoneyAsInt(avg_spend) },
        { label: 'Outcome variable', value: formatForecastViewLabel(kpiType, kpiLabel) }
      ]
    },
    {
      title: 'Recency',
      rows: [{ label: 'Last refresh date', value: dateToMddyy(lastRefreshDate) }]
    },
    {
      title: 'Statistics',
      rows: [
        {
          label: 'Spend Relationship',
          value: spend_relationship,
          tooltipText:
            'This is how spend was modeled relative to the outcome. “Linear” refers to a linear relationship; “Log” refers to a logarithmic relationship with diminishing returns.'
        },
        {
          label: 'Last Fold wMAPE',
          value: last_fold_wmape != null ? formatPercent(last_fold_wmape, 2) : EMPTY_VALUE,
          tooltipText:
            'The same weighted metric on only the most recent 30-day holdout fold — the out-of-sample check for this forecast.'
        },
        {
          label: 'Last Fold MAE',
          value: formatDecimal(last_fold_mae, 2),
          tooltipText:
            "Mean absolute error is a measure of the model's prediction accuracy, reflecting the average magnitude of the difference between actual and predicted values in absolute terms. Its definition is avg(abs(predicted - actual))."
        }
      ],
      subsections: [
        [
          {
            label: 'Train/Test Split',
            value: `${Math.round(train_split)}/${Math.round(test_split)}`,
            tooltipText:
              'This refers to the fraction of data used to fit the model vs the fraction held out for model validation. Setting aside data for model validation is very important in ensuring that the model generalizes well to new, unseen data.'
          },
          {
            label: 'Median wMAPE',
            value: median_wmape != null ? formatPercent(median_wmape, 2) : EMPTY_VALUE,
            tooltipText:
              "Weighted mean absolute percentage error, taken as the median across cross-validation folds. Each day's error is weighted by its actual volume (sum(|predicted − actual|) / sum(actual)), so low-volume days don't distort it; more robust than plain MAPE."
          },
          { label: 'Median MAE', value: formatDecimal(median_mae, 2) }
        ]
      ]
    },
    {
      title: `Key Forecasts for ${formatRawBudget(primaryBudget)}`,
      rows: keyForecastRows
    }
  ];
};

export const getForecastDataRanges: (
  forecastData: GetForecastDataResponse
) => ForecastDataTimestampRanges = forecastData => {
  const trainTestData = Object.values(forecastData.modelTrainTest).flat();
  const historicalData = Object.values(forecastData.actualGap).flat();
  const forecastResultsData = Object.values(forecastData.forecastResult).flat();

  const getMinMax = (acc, row) => ({
    min: Math.min(acc.min, row.timestamp),
    max: Math.max(acc.max, row.timestamp)
  });
  const minMaxInitAcc = { min: Infinity, max: -Infinity };

  const { min: trainTestMin, max: trainTestMax } = trainTestData.reduce(getMinMax, minMaxInitAcc);
  const { min: historicalMin, max: historicalMax } = historicalData.reduce(
    getMinMax,
    minMaxInitAcc
  );
  const { min: forecastResultsMin, max: forecastResultsMax } = forecastResultsData.reduce(
    getMinMax,
    minMaxInitAcc
  );

  return {
    trainTestTimestampRange: { min: trainTestMin, max: trainTestMax },
    historicalTimestampRange: { min: historicalMin, max: historicalMax },
    forecastResultsTimestampRange: { min: forecastResultsMin, max: forecastResultsMax }
  };
};

// KPI/outcome keys that actually have data, so callers can verify a key is real before keying into it.
export const getKpiOutcomeKeys = (forecastData: GetForecastDataResponse): Set<KpiOutcomeString> => {
  const {
    modelTrainTest: trainTestData,
    actualGap: historicalData,
    forecastResult: forecastResultsData
  } = forecastData;

  return new Set([
    ...(Object.keys(trainTestData ?? {}) as KpiOutcomeString[]),
    ...(Object.keys(historicalData ?? {}) as KpiOutcomeString[]),
    ...(Object.keys(forecastResultsData ?? {}) as KpiOutcomeString[])
  ]);
};

interface KpiOutcomeSelection {
  kpiName: string;
  outcomeVar: OutcomeVariableType;
}

// Resolves the KPI/outcome pair a forecast should actually display: the
// selected pair itself when it has data, else the first sibling in `kpis`
// that does, else undefined when none of them do.
export const getKpiWithData = (
  kpis: ForecastKpi[],
  availableKeys: Set<KpiOutcomeString>,
  selected: KpiOutcomeSelection
): KpiOutcomeSelection | undefined => {
  if (
    availableKeys.has(getKpiOutcomeString({ outcome: selected.outcomeVar, kpi: selected.kpiName }))
  ) {
    return selected;
  }

  const fallback = kpis.find(kpi =>
    availableKeys.has(getKpiOutcomeString({ outcome: kpi.type, kpi: kpi.name }))
  );

  return fallback && { kpiName: fallback.name, outcomeVar: fallback.type };
};

export const flattenForecastData: (
  forecastData: GetForecastDataResponse
) => Record<KpiOutcomeString, FlattenedForecastDataResult> = forecastData => {
  const {
    modelTrainTest: trainTestData,
    actualGap: historicalData,
    forecastResult: forecastResultsData
  } = forecastData;

  const output: Record<KpiOutcomeString, FlattenedForecastDataResult> = {};

  if (!trainTestData || !historicalData || !forecastResultsData) {
    return output;
  }

  // Ensure both the historical and forecasted data share the same set of kpis
  const kpiOutcomeKeys = getKpiOutcomeKeys(forecastData);

  kpiOutcomeKeys.forEach(key => {
    const trainTestDataForKpi = trainTestData[key];
    const historicalDataForKpi = historicalData[key];
    const forecastResultsDataForKpi = forecastResultsData[key];

    const flatTrainTestData = trainTestDataForKpi.map(
      ({ date, timestamp, actual_kpi, spend, cpm }) => {
        // CPA = Spend / KPI Volume, ROAS = KPI Revenue / Spend
        const actual_efficency = key.endsWith('Volume') ? spend / actual_kpi : actual_kpi / spend;

        return {
          date,
          timestamp,
          [`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES.Total}`]: actual_kpi,
          [`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES.Efficiency}`]: actual_efficency,
          [`${HISTORICAL_KEY}-spend`]: spend,
          [`${HISTORICAL_KEY}-cpm`]: cpm
        };
      }
    ) as unknown as FlatForecastData<string>[];

    const flatHistoricalData = historicalDataForKpi.map(
      ({ date, timestamp, actual_kpi, spend, cpm }) => {
        // CPA = Spend / KPI Volume, ROAS = KPI Revenue / Spend
        const actual_efficency = key.endsWith('Volume') ? spend / actual_kpi : actual_kpi / spend;

        return {
          date,
          timestamp,
          [`${GAP_KEY}-${KPI_VIEW_SUFFICES.Total}`]: actual_kpi,
          [`${GAP_KEY}-${KPI_VIEW_SUFFICES.Efficiency}`]: actual_efficency,
          [`${GAP_KEY}-spend`]: spend,
          [`${GAP_KEY}-cpm`]: cpm
        };
      }
    ) as unknown as FlatForecastData<string>[];

    const forecastResultsDataByDate = forecastResultsDataForKpi.reduce<
      Record<string, ForecastResultsData[]>
    >((acc, row) => {
      (acc[row.date] ??= []).push(row);
      return acc;
    }, {});

    const flatForecastResultsData: FlatForecastData<string>[] = Object.keys(
      forecastResultsDataByDate
    ).map(date => {
      const data: ForecastResultsData[] = forecastResultsDataByDate[date];
      const start = { date: data[0].date, timestamp: data[0].timestamp };

      return data.reduce(
        (
          acc,
          {
            budget,
            predicted_kpi,
            predicted_kpi_ci,
            predicted_efficiency,
            predicted_efficiency_ci,
            spend,
            cpm
          }
        ) => ({
          ...acc,
          [`${budget}-${KPI_VIEW_SUFFICES.Total}`]: predicted_kpi,
          [`${budget}-${KPI_CI_VIEW_SUFFICES.Total}`]: predicted_kpi_ci,
          [`${budget}-${KPI_VIEW_SUFFICES.Efficiency}`]: predicted_efficiency,
          [`${budget}-${KPI_CI_VIEW_SUFFICES.Efficiency}`]: predicted_efficiency_ci,
          [`${budget}-spend`]: spend,
          [`${budget}-cpm`]: cpm
        }),
        start
      );
    });

    output[key] = {
      trainTestData: flatTrainTestData,
      historicalData: flatHistoricalData,
      forecastResultsData: flatForecastResultsData
    };
  });

  return output;
};

interface IsForecastLastInGroupInput {
  forecastId: string;
  groupForecastIds: string[];
}

// Determines if the given forecastId is the last visible row of its group of forecasts
export const isLastVisible = ({
  forecastId,
  groupForecastIds
}: IsForecastLastInGroupInput): boolean => {
  const foundIndex = groupForecastIds.findIndex(id => id === forecastId);
  return foundIndex === groupForecastIds.length - 1;
};

// Shared by Saved Forecasts and the Request Forecast form so a pooled ("") platform/
// tactic and a real, explicitly-selected "N/A" (Untagged) tactic can't render
// differently between the two.
export const UNTAGGED_TACTIC_LABEL = 'Untagged';
export const ALL_PLATFORMS_LABEL = 'All platforms';
export const ALL_TACTICS_LABEL = 'All tactics';

export const tacticDisplayLabel = (tactic: string): string =>
  tactic === 'N/A' ? UNTAGGED_TACTIC_LABEL : tactic;

export const pooledPlatformLabel = (platform: string | null): string =>
  platform || ALL_PLATFORMS_LABEL;
export const pooledTacticLabel = (tactic: string | null): string =>
  tactic ? tacticDisplayLabel(tactic) : ALL_TACTICS_LABEL;

export const getSelectablePropertyFromForecast: (
  forecast: FullForecastSelection,
  propertyName: SelectableForecastPropertyValue
) => string = (forecast, propertyName) => {
  const { channel, platform, outcomeVar, kpiName, groupId } = forecast;

  switch (propertyName) {
    case SelectableForecastProperty.CHANNEL_PLATFORM:
      return getCombinedChannelPlatform(channel, platform);
    case SelectableForecastProperty.OUTCOME_VARIABLE:
      return outcomeVar;
    case SelectableForecastProperty.KPI:
      return kpiName;
    case SelectableForecastProperty.FORECAST_GROUP:
      return groupId;
    default:
      return '';
  }
};

interface ForecastGroupMatchTarget {
  channel: string;
  platform: string | null;
  kpiName: string;
  outcomeVar: OutcomeVariableType;
}

interface ForecastGroupMatch {
  forecastId: string;
  kpiName: string;
  outcomeVar: OutcomeVariableType;
}

// Falls back to the group's first forecast/kpi when no exact match exists;
// returns undefined if the group has neither.
export const getExactOrFirstForecastMatchInGroup = (
  group: ForecastGroup,
  target: ForecastGroupMatchTarget
): ForecastGroupMatch | undefined => {
  const forecast =
    group.forecasts.find(f => f.channel === target.channel && f.platform === target.platform) ??
    group.forecasts[0];
  const kpi =
    group.kpis.find(k => k.name === target.kpiName && k.type === target.outcomeVar) ??
    group.kpis[0];

  if (!forecast || !kpi) {
    return undefined;
  }

  return { forecastId: forecast.id, kpiName: kpi.name, outcomeVar: kpi.type };
};

// Uses URLSearchParams so KPI names with reserved characters can't corrupt the URL.
export const buildForecastSelectionUrl = (
  pathname: string,
  selection: ForecastGroupMatch
): string => {
  const params = new URLSearchParams({
    forecastId: selection.forecastId,
    kpiName: selection.kpiName,
    outcomeVar: selection.outcomeVar
  });
  return `${pathname}?${params.toString()}`;
};

export const buildFullForecastSelection: (
  group: ForecastGroup,
  forecast: Forecast,
  kpi: ForecastKpi
) => FullForecastSelection = (group, forecast, kpi) => {
  const forecastGroupCopy: Omit<ForecastGroup, 'forecasts'> & { forecasts?: Forecast[] } = {
    ...group
  };
  delete forecastGroupCopy.forecasts;

  const forecastCopy: Omit<Forecast, 'kpis'> & { kpis?: ForecastKpi[] } = {
    ...forecast
  };
  delete forecastCopy.kpis;

  return { ...forecastGroupCopy, ...forecastCopy, kpiName: kpi.name, outcomeVar: kpi.type };
};

export const extractForecastValsFromQueryParams = (
  search: string
): {
  forecastId: string | undefined;
  kpiName: string | undefined;
  outcomeVar: OutcomeVariableType | undefined;
} => {
  const params = new URLSearchParams(search);
  const forecastIdQueryParam = params.get('forecastId');
  const kpiNameQueryParam = params.get('kpiName');
  const outcomeVarQueryParam = params.get('outcomeVar');

  const forecastId = forecastIdQueryParam ?? undefined;
  const kpiName = kpiNameQueryParam ?? undefined;
  const outcomeVar =
    outcomeVarQueryParam !== null && ['Volume', 'Revenue'].includes(outcomeVarQueryParam)
      ? (outcomeVarQueryParam as OutcomeVariableType)
      : undefined;

  return { forecastId, kpiName, outcomeVar };
};

export const extractGroupIdFromQueryParams = (search: string): string | undefined =>
  new URLSearchParams(search).get('groupId') ?? undefined;

// Uses URLSearchParams so a groupId with reserved characters can't corrupt the URL.
export const buildGroupSelectionUrl = (
  pathname: string,
  groupId: string,
  outcomeVar?: OutcomeVariableType
): string => {
  const params = new URLSearchParams({ groupId });
  if (outcomeVar) {
    params.set('outcomeVar', outcomeVar);
  }
  return `${pathname}?${params.toString()}`;
};

// Single source for the date-range dash treatment, so callers with already-formatted strings
// (e.g. table rows) and callers with raw dates (getCombinedDateRange) can't drift apart.
export const joinDateRange = (formattedStart: string, formattedEnd: string): string =>
  `${formattedStart} – ${formattedEnd}`;

export const getCombinedDateRange = (startDate: BpmDateString, endDate: BpmDateString): string =>
  joinDateRange(dateToMddyy(startDate), dateToMddyy(endDate));

/**
 * Maps each forecast group's id to its dropdown display label: date range,
 * abbreviated id, and last-modified instant, so no two options can ever
 * render identically, even when two groups share the same date range.
 */
export const getForecastGroupLabels = (groups: ForecastGroup[]): Record<ForecastGroupId, string> =>
  Object.fromEntries(
    groups.map(group => {
      const dateRange = getCombinedDateRange(group.startDate, group.endDate);
      const timestamp = instantToUtcTimestamp(group.lastModifiedTimestamp);
      return [group.groupId, `${dateRange} · ${group.groupId.slice(0, 8)} · ${timestamp}`];
    })
  );

// Drops the deleted forecast from its group, and drops the group entirely if that was its last
// forecast. Re-derives membership only -- kpis/lastModifiedTimestamp aren't recomputed.
export const removeForecastFromGroups = (
  groups: ForecastGroup[] | undefined,
  deletedForecastId: ForecastId
): ForecastGroup[] | undefined =>
  groups
    ?.map(group => ({
      ...group,
      forecasts: group.forecasts.filter(forecast => forecast.id !== deletedForecastId)
    }))
    .filter(group => group.forecasts.length > 0);

export const getKpiFormatter = (
  outcomeVar: OutcomeVariableType,
  view: ForecastKpiViewOption = ForecastKpiViewOption.TOTAL
): ((value: number) => string) => {
  if (outcomeVar === 'Volume' && view === ForecastKpiViewOption.TOTAL) {
    // Total KPI Volume
    return val => VOLUME_FORMATTER(val, { zeroLabel: '0' });
  }
  if (outcomeVar === 'Revenue' && view === ForecastKpiViewOption.TOTAL) {
    // Total Revenue KPI
    return val => SPEND_OR_REVENUE_FORMATTER(val, { zeroLabel: '$0', min: 0 });
  }
  // Both efficiency metrics (CPA & ROAS)
  return val => CPX_FORMATTER(val, { zeroLabel: '$0' });
};

// Abbreviate $ numbers in y-axes, but show full values in other locations
export const getKpiAxisFormatter = (
  outcomeVar: OutcomeVariableType,
  view: ForecastKpiViewOption = ForecastKpiViewOption.TOTAL
): ((value: number) => string) => {
  if (outcomeVar === 'Volume' && view === ForecastKpiViewOption.TOTAL) {
    return (val: number): string => abbreviateNumber(val);
  }
  return formatAxisSpend;
};

export const getCiFormatter = (
  outcomeVar: OutcomeVariableType,
  view: ForecastKpiViewOption = ForecastKpiViewOption.TOTAL
): ((value: [number, number]) => string) => {
  const formatKpi = getKpiFormatter(outcomeVar, view);

  return val => `${formatKpi(val[0])}–${formatKpi(val[1])}`;
};

// comparisonBudgets may include an entry equal to the primary (the 100% scale).
export const excludePrimaryBudget = (budgets: number[], primaryBudget: number): number[] =>
  budgets.filter(budget => budget !== primaryBudget);

export const dedupeBudgetsWithPrimary = (budgets: number[], primaryBudget: number): number[] =>
  [...new Set([...excludePrimaryBudget(budgets, primaryBudget), primaryBudget])].sort(
    (a, b) => a - b
  );

export const getForecastDownloadFilename = (
  forecast: FullForecastSelection,
  cid: string,
  prefix = 'Forecast'
): string =>
  `${prefix}_${cid}_${forecast.channel}${forecast.platform ? `_${forecast.platform}` : ''}_${
    forecast.kpiName
  }_${forecast.outcomeVar}_${dateToMddyyDashed(forecast.startDate)}_to_${dateToMddyyDashed(
    forecast.endDate
  )}`.replace(/\s/g, '-');

interface TransformAndDownloadDataToCSVInput {
  forecast: FullForecastSelection;
  data: FlattenedForecastDataResult;
  includeHistorical: boolean;
  cid: string;
}

// Convert from wide format to long format for download
export const transformAndDownloadDataToCSV = ({
  forecast,
  data,
  includeHistorical,
  cid
}: TransformAndDownloadDataToCSVInput): void => {
  const { trainTestData, historicalData: gapData, forecastResultsData: forecastedData } = data;
  const allBudgets = dedupeBudgetsWithPrimary(forecast.comparisonBudgets, forecast.primaryBudget);
  const formatTotalKpi = getKpiFormatter(forecast.outcomeVar, ForecastKpiViewOption.TOTAL);
  const formatEfficiencyKpi = getKpiFormatter(
    forecast.outcomeVar,
    ForecastKpiViewOption.EFFICIENCY
  );

  const kpiKey = formatForecastViewLabel(
    forecast.outcomeVar,
    forecast.kpiName,
    ForecastKpiViewOption.TOTAL
  );
  const kpiLowerCIKey = `${kpiKey}, Lower CI`;
  const kpiUpperCIKey = `${kpiKey}, Upper CI`;
  const efficiencyKey =
    ForecastChartViewLabels[forecast.outcomeVar][ForecastKpiViewOption.EFFICIENCY];
  const efficiencyLowerCIKey = `${efficiencyKey}, Lower CI`;
  const efficiencyUpperCIKey = `${efficiencyKey}, Upper CI`;

  const flatMapHistorical = key => row => ({
    Date: row.date,
    'Percent of Primary Budget': EMPTY_VALUE,
    'Budget Value': EMPTY_VALUE,
    Spend: formatSpend(row[`${key}-spend`]),
    CPM: formatCpm(row[`${key}-cpm`]),
    [kpiKey]: formatTotalKpi(row[`${key}-${KPI_VIEW_SUFFICES.Total}`]),
    [kpiLowerCIKey]: EMPTY_VALUE,
    [kpiUpperCIKey]: EMPTY_VALUE,
    [efficiencyKey]: formatEfficiencyKpi(row[`${key}-${KPI_VIEW_SUFFICES.Efficiency}`]),
    [efficiencyLowerCIKey]: EMPTY_VALUE,
    [efficiencyUpperCIKey]: EMPTY_VALUE
  });

  const flatHistoricalData = includeHistorical
    ? [
        ...trainTestData.flatMap(flatMapHistorical(HISTORICAL_KEY)),
        ...gapData.flatMap(flatMapHistorical(GAP_KEY))
      ]
    : [];

  const flatForecastedData = forecastedData.flatMap(row =>
    allBudgets.map(budget => {
      const confidenceInterval = row[`${budget}-${KPI_CI_VIEW_SUFFICES.Total}`] ?? [0, 0];
      const efficiencyConfidenceInterval = row[`${budget}-${KPI_CI_VIEW_SUFFICES.Efficiency}`] ?? [
        0, 0
      ];
      return {
        Date: row.date,
        'Percent of Primary Budget': formatPercentBudget(budget / forecast.primaryBudget),
        'Budget Value': formatMoneyAsInt(budget),
        Spend: formatSpend(row[`${budget}-spend`]),
        CPM: formatCpm(row[`${budget}-cpm`]),
        [kpiKey]: formatTotalKpi(row[`${budget}-${KPI_VIEW_SUFFICES.Total}`]),
        [kpiLowerCIKey]: formatTotalKpi(confidenceInterval[0]),
        [kpiUpperCIKey]: formatTotalKpi(confidenceInterval[1]),
        [efficiencyKey]: formatEfficiencyKpi(row[`${budget}-${KPI_VIEW_SUFFICES.Efficiency}`]),
        [efficiencyLowerCIKey]: formatEfficiencyKpi(efficiencyConfidenceInterval[0]),
        [efficiencyUpperCIKey]: formatEfficiencyKpi(efficiencyConfidenceInterval[1])
      };
    })
  );

  const csvRows = [...flatHistoricalData, ...flatForecastedData];
  const prefix = includeHistorical ? 'Forecast+Historical' : 'Forecast';
  const fileName = getForecastDownloadFilename(forecast, cid, prefix);

  downloadJSONToCSV(csvRows, fileName);
};

const OUTCOME_LABELS: Record<Pairing, string> = {
  spend_volume: 'Volume',
  spend_revenue: 'Revenue'
};

export const pairingForOutcome = (outcome: string): Pairing =>
  outcome.toLowerCase() === 'revenue' ? 'spend_revenue' : 'spend_volume';

export const formatOutcomeLabel = (kpi: string, outcome: Pairing) =>
  `${kpi} ${OUTCOME_LABELS[outcome]}`.trim();

export const formatForecastViewLabel = (
  outcomeVar: OutcomeVariableType,
  kpiName: string,
  view: ForecastKpiViewOption = ForecastKpiViewOption.TOTAL
): string =>
  view === ForecastKpiViewOption.TOTAL
    ? formatOutcomeLabel(kpiName, pairingForOutcome(outcomeVar))
    : ForecastChartViewLabels[outcomeVar][view];
